/* eslint-disable react/no-unescaped-entities */
/**
 * /contact — Page Contact CONFILEX (SEO + entrée commerciale).
 *
 * Cette page ne crée PAS de canal téléphone/email direct (positionnement
 * institutionnel : tout passe par la qualification documentaire).
 * Elle oriente vers :
 *   - Orientation intelligente (qualification gratuite)
 *   - Mission Premium 89 € TTC
 *   - Devenir spécialiste référencé
 */
import { Link } from "react-router-dom";
import { Mail, Compass, ShieldCheck, Users, ArrowRight, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo, { ldBreadcrumb } from "@/components/Seo";


export default function Contact() {
  return (
    <>
      <Seo
        title="Contact CONFILEX — Orientation institutionnelle"
        description="Contacter CONFILEX : orientation gratuite, mission de recherche premium, partenariat spécialiste. Plateforme française d'orientation et d'audit documentaire intelligent."
        canonical="/contact"
        keywords={["contact confilex", "contact CONFILEX", "joindre confilex"]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "CONFILEX",
            "url": "https://confilex.fr",
            "logo": "https://confilex.fr/logo-icon.svg",
            "contactPoint": [{
              "@type": "ContactPoint",
              "contactType": "customer support",
              "areaServed": "FR",
              "availableLanguage": ["fr"],
              "url": "https://confilex.fr/contact",
            }],
          },
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Contact", url: "/contact" },
          ]),
        ]}
      />

      <main className="bg-paper min-h-screen" data-testid="contact-page">
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              CONFILEX · CONTACT INSTITUTIONNEL
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="contact-h1">
              Comment souhaitez-vous être accompagné ?
            </h1>
            <p className="text-paper/75 text-base max-w-3xl leading-relaxed">
              CONFILEX ne dispose pas d'un canal téléphonique commercial. Toute prise
              de contact s'amorce par une qualification documentaire de votre besoin,
              afin que les profils proposés soient strictement pertinents.
            </p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          <div className="grid sm:grid-cols-3 gap-5">
            <Card className="p-6 bg-paper border-sage hover:border-copper transition-smooth" data-testid="contact-orientation">
              <Compass className="w-6 h-6 text-copper mb-3" />
              <h2 className="font-serif italic text-xl text-night mb-2">Orientation intelligente</h2>
              <p className="text-sm text-night/75 mb-4">
                Qualifiez votre besoin en quelques minutes. L'IA d'orientation
                CONFILEX vous propose ensuite des profils potentiellement pertinents.
              </p>
              <Link to="/orientation-intelligente">
                <Button className="bg-night text-paper hover:bg-night/90 rounded-sm w-full">
                  Démarrer l'orientation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-paper border-copper/40" data-testid="contact-premium">
              <p className="text-[10px] uppercase tracking-[0.2em] text-copper mb-2 font-semibold">RECOMMANDÉ</p>
              <Users className="w-6 h-6 text-copper mb-3" />
              <h2 className="font-serif italic text-xl text-night mb-2">Mission de Recherche Premium</h2>
              <p className="text-sm text-night/75 mb-4">
                89 € TTC — qualification documentaire, identification des compétences
                requises et transmission à au moins 4 profils potentiellement pertinents.
              </p>
              <Link to="/orientation-intelligente?intent=premium">
                <Button className="bg-copper text-paper hover:bg-copper/90 rounded-sm w-full">
                  Lancer une Mission Premium
                </Button>
              </Link>
            </Card>

            <Card className="p-6 bg-paper border-sage hover:border-copper transition-smooth" data-testid="contact-verifier">
              <ScanLine className="w-6 h-6 text-copper mb-3" />
              <h2 className="font-serif italic text-xl text-night mb-2">Audit documentaire</h2>
              <p className="text-sm text-night/75 mb-4">
                Faire vérifier vos documents avant toute consultation. Lecture
                factuelle, détection de divergences, questions à préparer.
              </p>
              <Link to="/verifier">
                <Button variant="outline" className="border-night text-night hover:bg-night hover:text-paper rounded-sm w-full">
                  Vérifier mes documents
                </Button>
              </Link>
            </Card>
          </div>
        </section>

        <section className="bg-ivory border-t border-sage">
          <div className="max-w-3xl mx-auto px-6 py-14">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">
              VOUS ÊTES PROFESSIONNEL ?
            </p>
            <h2 className="font-serif italic text-3xl text-night mb-4">
              Rejoindre le réseau de spécialistes CONFILEX
            </h2>
            <p className="text-sm text-night/75 leading-relaxed mb-5">
              Avocats inscrits à un Barreau français, experts-comptables inscrits à l'Ordre,
              notaires en exercice, médiateurs qualifiés, commissaires de justice et conseillers
              en gestion de patrimoine : CONFILEX référence des professionnels institutionnels
              après qualification documentaire de leur inscription et de leur activité.
            </p>
            <Link to="/rejoindre">
              <Button className="bg-night text-paper hover:bg-night/90 rounded-sm" data-testid="cta-rejoindre">
                Candidater au réseau <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-2 font-semibold">PRESSE & MÉDIAS</p>
                <p className="text-sm text-paper/85 leading-relaxed">
                  Pour toute demande presse, merci de passer par le formulaire d'orientation
                  en sélectionnant « Presse » dans le motif de contact.
                </p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-2 font-semibold">PARTENARIATS</p>
                <p className="text-sm text-paper/85 leading-relaxed">
                  Institutionnels, écoles, ordres professionnels : nous étudions les
                  propositions de partenariat via le canal « Orientation » avec mention
                  explicite du contexte.
                </p>
              </div>
              <div>
                <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-2 font-semibold">CONFIDENTIALITÉ</p>
                <p className="text-sm text-paper/85 leading-relaxed">
                  Toutes les communications avec CONFILEX sont chiffrées en transit
                  et traitées dans le respect du RGPD (Art. 32).
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-8 flex items-start gap-3 text-sm text-night/75">
            <ShieldCheck className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <p>
              CONFILEX n'est pas un cabinet d'avocats, d'experts-comptables, de notaires ni
              de conseil fiscal. Les professionnels référencés exercent en toute indépendance
              et conservent l'entière responsabilité de leur analyse et de leurs recommandations.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
